import { useEffect, useState } from 'react';
import { getHardwarePermissionStates, isBrowser, isPermissionsApiAvailable } from 'src/utils/device-utils';

/**
 * Listen to permission status change event and update
 * It currently doesn't support Safari and browser which doesn't support Permissions API
 * Especially we don't want to run getHardwarePermissionStates on Safari version before 16 because it will trigger device permission popups
 */

export const useDevicePermissionState = (permissionName: 'camera' | 'microphone'): PermissionState | null => {
  const [permissionState, setPermissionState] = useState<PermissionState | null>(null);
  useEffect(() => {
    if (isBrowser('safari') || !isPermissionsApiAvailable) {
      return;
    }
    const handleOnPermissionChange = (e: Event & { currentTarget?: { state?: any } }) => {
      const newState = e.currentTarget?.state;
      setPermissionState(newState);
    };

    let queryResult: PermissionStatus;
    const addListenerOnPermissionChange = async () => {
      try {
        queryResult = await navigator.permissions.query({ name: permissionName as PermissionName });
        // TODO: Safari doesn't support this event.
        // https://developer.mozilla.org/en-US/docs/Web/API/PermissionStatus/change_event#browser_compatibility
        queryResult.addEventListener('change', handleOnPermissionChange);
      } catch (e) {
        console.error('Error while adding listener on permission change. Check browser compatibility.', e);
      }
    };
    void addListenerOnPermissionChange();

    const getCurrentDeviceState = async () => {
      const deviceStates = await getHardwarePermissionStates();
      setPermissionState(
        permissionName === 'camera' ? deviceStates.cameraPermissionState : deviceStates.microphonePermissionState,
      );
    };

    void getCurrentDeviceState();

    return () => {
      if (!queryResult) {
        return;
      }
      queryResult.removeEventListener('change', handleOnPermissionChange);
    };
  }, [permissionName]);

  return permissionState;
};
